/**
 * This is an extension to Leaflet allowing to display the TomTom traffic layer. It is based on the raster
 * L.TileLayer implementation.
 *
 * @class TomTomTrafficLayer
 * @extends L.TileLayer
 */
/**
 * @method tomTomTrafficLayer
 * @param {Object} [options] All the options that Leaflet allows, as described here:
 *     http://leafletjs.com/reference.html#tilelayer-options Additional options, which are TomTom specific, are
 *     described below.
 * @param {String} [options.key] This is an API key that can be obtained form http://developer.tomtom.com This is a key
 *     for the traffic services.
 * @param {String} [options.style] The traffic layer style to be used. Should be set to one of the following values:
 *     &lt;'s1', 's2', 's3', 'night'&gt;. By default, the _night_ style is used.
 * @param {Number} [options.opacity=0.8] The traffic incidents layer's opacity, by default set to 80%. A
 *     floating point value between 0 and 1 have to be used, where 1 means no transparency.
 */

/**
 * This is an extension to Leaflet allowing to display the TomTom traffic incidents layer. It is based on the L.GeoJSON
 * class. It is using the tomtom.trafficIncidents service in order to retrieve incidents details.
 *
 * A look and feel, together with a clustering behaviour, can be modified by passing the pointToLayer function. The
 * default implementation looks as follows
 * ```javascript
 *
 * define(['leaflet', 't', 'unitFormatConverter/unitFormatConverter'], function (L, T, units) {
 *   'use strict';
 *
 *   var iconsMapping = {
 *           'Unknown': 'danger', 'Accident': 'accident', 'Fog': 'danger', 'Dangerous Conditions':
 *           'danger', 'Rain': 'danger', 'Ice': 'ice', 'Jam': 'incident', 'Lane Closed':
 *           'laneclosed', 'Road Closed': 'roadclosed', 'Road Works': 'roadwork', 'Wind': 'danger',
 *           'Flooding': 'danger', 'Detour': 'danger', 'Cluster': ''
 *       },
 *       iconTemplate = '<div class="traffic-icon"><div class="icon-circle-{{=incidentSeverity}}">' +
 *           '<div class="icon-{{=icon}}-white"></div></div></div>',
 *       shieldTemplate = '{{roadNumber}}<div class="road-shield">{{=roadNumber}}</div>' +
 *       '{{/roadNumber}}',
 *       descriptionTemplate = '<div class="traffic-description"><div class="incident-category">' +
 *       '{{=incidentCategory}}</div><div class="incident-delay-length"{{!delaySeconds}}' +
 *       '{{!lengthMeters}} style="display:none;"{{/!lengthMeters}}{{/!delaySeconds}}>' +
 *       '{{delaySeconds}}<div class="incident-delay">{{=time}}</div>{{/delaySeconds}}' +
 *       '{{lengthMeters}}<div class="incident-length">{{=distance}}</div>{{/lengthMeters}}</div>' +
 *       '</div>',
 *       messageTemplate = new T('<div class="traffic-details">' + iconTemplate + shieldTemplate +
 *       descriptionTemplate + '</div>');
 *
 *   function trafficIncidentMessage(info, imperialDistance) {
 *       info.distance = units.formatDistance(info.lengthMeters, imperialDistance);
 *       info.time = units.formatTime(info.delaySeconds);
 *       info.incidentSeverity = info.incidentSeverity || 'unknown';
 *       info.icon = iconsMapping[info.incidentCategory];
 *       return messageTemplate.render(info);
 *   }
 *
 *   function clusterClickHandler(bounds, latlng) {
 *       if (bounds) {
 *           return function () {
 *               this._map.fitBounds(bounds);
 *           };
 *       } else {
 *           return function () {
 *               this._map.setZoomAround(latlng, this._map.getZoom() + 2);
 *           };
 *       }
 *
 *   }
 *
 *   return function (feature, latlng, imperialDistance) {
 *       var iconOptions = {
 *           background: {
 *               iconSize: L.Browser.retina ? [34, 34] : [20, 20],
 *               iconAnchor: L.Browser.retina ? [17, 17] : [10, 10],
 *               icon: ''
 *           },
 *           icon: {
 *               iconSize: L.Browser.retina ? [34, 34] : [20, 20],
 *               iconAnchor: [0, 0],
 *               icon: ''
 *           },
 *           content: {
 *               iconSize: L.Browser.retina ? [34, 34] : [20, 20],
 *               iconAnchor: [0, 0],
 *               content: '',
 *               'style': {
 *                   'color': '#fff',
 *                   'font-weight': 'bold',
 *                   'font-size': L.Browser.retina ? '18px' : '11px'
 *               }
 *           },
 *           popupAnchor: L.Browser.retina ? [0, -8] : [0, -5]
 *       }, marker;
 *
 *       if (!feature || !feature.properties) {
 *           return null;
 *       }
 *
 *       iconOptions.background.icon = 'icon-circle-' +
 *           (feature.properties.incidentSeverity || 'unknown');
 *
 *       if (feature.properties.clusterSize) {
 *           iconOptions.icon = null;
 *           iconOptions.content.content = feature.properties.clusterSize < 100
 *              ? feature.properties.clusterSize:'99+';
 *           marker = L.marker(latlng, {icon: L.svgIcon(iconOptions)}); marker.on('click',
 *               clusterClickHandler(feature.properties.clusterBounds, latlng));
 *       } else {
 *           iconOptions.content = null;
 *           iconOptions.icon.icon = 'icon-' + iconsMapping[feature.properties.incidentCategory] +
  *              '-white';
 *           marker = L.marker(latlng, {icon: L.svgIcon(iconOptions)});
 *           marker.bindPopup(trafficIncidentMessage(feature.properties, imperialDistance),
 *              {className: 'incidents-popup'});
 *       }
 *       return marker;
 *   };
 * });
 * ```
 *
 * @class TomTomTrafficIncidentsLayer
 * @extends L.GeoJSON
 */
/**
 * @method tomTomTrafficIncidentsLayer
 * @param {Object} [options] All the options that Leaflet allows, as described here:
 *     http://leafletjs.com/reference.html#geojson-options Additional options, which are TomTom specific, are described
 *     below.
 * @param {String} [options.key] This is an API key that can be obtained form http://developer.tomtom.com This is
 *     a key for the traffic services.
 * @param {Function} [options.pointToLayer=default] This function is used to convert vector traffic
 *     incidents into a layer of markers on the map. It takes the GeoJSON feature as the first parameter and the latlng
 *     object as the second one. It returns a layer that will be added to the map. If this is omitted, the default
 *     implementation is used (displayed above).
 * @param {Boolean} [options.imperialDistance] With this parameter, you can make the traffic details popups to display
 *     a distance in imperial units, if the default pointToLayer function is used.
 */
define('display/layers/trafficLayer',["leaflet", "security", "services/trafficModelId", "services/trafficIncidents", "./defaultIncidentsLayer", "http"],
    function (L, security, trafficModelId, trafficIncidents, defaultPointToLayer, http) {
        "use strict";

        var trafficStyles = ["s1", "s2", "s3", "night"];

        function getTrafficStyle(trafficLayer) {
            var style = "night";
            if (typeof trafficLayer === "string") {
                for (var i = 0; i < trafficStyles.length; i += 1) {
                    if (trafficStyles[i] === trafficLayer.toLowerCase()) {
                        return trafficStyles[i];
                    }
                }
            }
            return style;
        }

        function pointToLayer(options) {
            return function (feature, latlng) {
                return defaultPointToLayer(feature, latlng, options.imperialDistance);
            };
        }

        L.TomTomTrafficIncidentsLayer = L.GeoJSON.extend({
            options: {
                imperialDistance: false,
                trafficModelId: -1
            },
            initialize: function (options) {
                L.setOptions(this, options);
                this._layers = {};
                security.setKey("services/traffic", options.key);
                this.incidentsService = trafficIncidents()
                    .key(security.getKey("services/traffic"))
                    .style(getTrafficStyle(options.style));
                this.options.pointToLayer = this.options.pointToLayer || pointToLayer(this.options);
            },
            onAdd: function (map) {
                this._map = map;
                this.eachLayer(map.addLayer, map);
                map.on({"viewreset": this.redraw, "moveend": this.update}, this);
            },
            redraw: function () {
                var that = this,
                    map = this._map;
                this.incidentsService.boundingBox(map.getBounds()).zoomLevel(map.getZoom()).trafficModelID(this.options.trafficModelId).go().then(function (geoJSON) {
                    that.clearLayers().addData(geoJSON);
                });
            },
            update: function () {
                var that = this,
                    map = this._map;
                this.incidentsService.boundingBox(map.getBounds()).zoomLevel(map.getZoom()).go().then(function (geoJSON) {
                    that.addData(geoJSON);
                    var positions = [];
                    that.eachLayer(function (layer) {
                        var latLng = layer.getLatLng();
                        var position = latLng.lat + "," + latLng.lng;
                        for (var i = 0; i < positions.length; i += 1) {
                            if (position === positions[i]) {
                                that.removeLayer(layer);
                                return;
                            }
                        }
                        positions.push(position);
                    });
                });
            },
            setTrafficModelId: function (trafficModelId) {
                if (this.options.trafficModelId !== trafficModelId) {
                    this.options.trafficModelId = trafficModelId;
                    this.redraw();
                }
            }
        });

        L.tomTomTrafficIncidentsLayer = function (options) {
            return new L.TomTomTrafficIncidentsLayer(options);
        };

        function handleSubdomains(options) {
            if (typeof options.subdomains === "string") {
                options.subdomains = options.subdomains.split("");
            }
        }

        function loadAnimation() {
            var opacity = "opacity",
                back = this._bgBuffer;
            back.style[L.DomUtil.TRANSITION] = opacity + " 10";
            back.style[opacity] = 0.1;
            this._tileContainer.style[opacity] = this.options.opacity;
        }

        L.TomTomTrafficLayer = L.TileLayer.extend({
            options: {
                attribution: "",
                minZoom: 2,
                maxZoom: 18,
                subdomains: "abcd",
                reuseTiles: true,
                opacity: 0.8,
                errorTileUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQAAAAEAAQMAAABmvDolAAAAA1BMVEUAAACnej3aAAAAAXRSTlMAQObYZgAAAB9JREFUGBntwQENAAAAwiD7p34ON2AAAAAAAAAAAOcCIQAAAfWivQQAAAAASUVORK5CYII=",
                detectRetina: false,
                trafficModelId: -1
            },
            initialize: function (options) {
                options = L.setOptions(this, options);
                if (options.bounds) {
                    options.bounds = L.latLngBounds(options.bounds);
                }
                security.setKey("services/traffic", options.key);
                var key = security.getKey("services/traffic");
                options.accessToken = key;
                this._url = http.protocol() + "://{s}.api.tomtom.com/traffic/map/4/tile/incidents/{style}/{z}/{x}/{y}.png?t={trafficModelId}" + (key ? "&key={accessToken}" : "");
                options.style = getTrafficStyle(options.style);
                handleSubdomains(options);
                options.tileSize = (options.detectRetina && L.Browser.retina) ? 512 : 256;
                if (options.tileSize === 512) {
                    options.zoomOffset -= 1;
                    options.minZoom = options.minZoom > 0 ? options.minZoom - 1 : 0;
                }
                this.setOpacity(options.opacity);
            },
            onAdd: function (map) {
                L.TileLayer.prototype.onAdd.call(this, map);
                if (this._animated) {
                    this.on("load", loadAnimation);
                }
                this._updateOpacity();
            },
            setTrafficModelId: function (trafficModelId) {
                if (this.options.trafficModelId !== trafficModelId) {
                    this.options.trafficModelId = trafficModelId;
                    this.redraw();
                }
            }
        });

        L.tomTomTrafficLayer = function (options) {
            return new L.TomTomTrafficLayer(options);
        };

        return function (options) {
            var raster = L.tomTomTrafficLayer(options),
                incidents = L.tomTomTrafficIncidentsLayer(options),
                trafficModelIdService = trafficModelId({key: security.getKey("services/traffic")});

            function refreshTrafficModelId() {
                trafficModelIdService.go().then(function (trafficModelId) {
                    raster.setTrafficModelId(trafficModelId);
                    incidents.setTrafficModelId(trafficModelId);
                });
            }

            refreshTrafficModelId();
            // refresh the traffic layer
            if (!options.noRefresh) {
                setInterval(refreshTrafficModelId, 30000);
            }
            return L.layerGroup([raster, incidents]);
        };
    });
